export type NormalizedArticleUrl = {
  href: string;
  path: string;
};

const allowedRoutePrefixes = new Set([
  "help",
  "vraagantwoord",
  "vraagantwoordafas",
  "content",
  "meldingen",
  "partner",
  "sales",
  "caohelp",
]);

export function normalizeArticleUrl(
  value: string,
  baseUrl: string | URL,
): NormalizedArticleUrl | null {
  const base = parseBaseUrl(baseUrl);
  const url = parseUrl(normalizeLegacyAuthorItHref(value), base);
  if (!url || !base) return null;
  if (!isHttpUrl(url) || !isHttpUrl(base)) return null;
  if (url.origin !== base.origin) return null;
  if (!isRenderableArticlePath(url.pathname)) return null;

  return {
    href: url.href,
    path: getPathSearchHash(url),
  };
}

export function getSafeSourceUrl(value: string, baseUrl: string | URL): string {
  const base = parseBaseUrl(baseUrl);
  const url = parseUrl(value, base);
  if (!url || !base) return "";
  if (!isHttpUrl(url) || !isHttpUrl(base)) return "";
  if (url.origin !== base.origin) return "";
  if (url.pathname.toLowerCase().startsWith("/cms")) return "";

  return getPathSearchHash(url);
}

export function getSafeExternalUrl(value: string): string {
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

// A fenced block (also an unterminated one, while an answer is still streaming) or an
// inline code span. Markdown inside these is sample text, not a link to rewrite.
const CODE_SEGMENT = /```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|(`+)[\s\S]*?\1/g;

const COPY_TEXT_LINK = /\]\(<([^>\n]+)>\)/g;

// The server renders every source pill in the copy text as `[label](<url>)` with an
// app-relative URL. Absolutize those against the page the app is served on, the same way
// the source panel copies an article link, so a pasted answer carries the public host
// (behind the Application Gateway the server only knows the internal ASE hostname).
// An already-absolute URL (an external source) is left as it is by the URL constructor,
// and code samples keep the link they literally show. A query the server put on the link
// rides along; the base URL's own query does not, so a ?domain country simulation is not
// carried over. That matches what the copy text has always done — the chat endpoints are
// called without that parameter, so the server never puts it on a copy link either — and
// a Live host has no override at all.
export function absolutizeCopyTextLinks(
  text: string,
  baseUrl: string | URL,
): string {
  const base = parseBaseUrl(baseUrl);
  if (!base) return text;

  return replaceOutsideCode(text, (markdown) =>
    markdown.replace(COPY_TEXT_LINK, (match, value: string) => {
      const url = parseUrl(value, base);
      return url ? `](<${url.href}>)` : match;
    }),
  );
}

// Applies `transform` to everything except the code segments, which are copied verbatim.
function replaceOutsideCode(
  text: string,
  transform: (markdown: string) => string,
): string {
  let result = "";
  let index = 0;

  for (const match of text.matchAll(CODE_SEGMENT)) {
    const start = match.index ?? 0;
    result += transform(text.slice(index, start)) + match[0];
    index = start + match[0].length;
  }

  return result + transform(text.slice(index));
}

function parseBaseUrl(baseUrl: string | URL): URL | null {
  try {
    return baseUrl instanceof URL ? baseUrl : new URL(baseUrl);
  } catch {
    return null;
  }
}

function parseUrl(value: string, baseUrl: URL | null): URL | null {
  const trimmedValue = value.trim();
  if (!trimmedValue || !baseUrl) return null;

  try {
    return new URL(trimmedValue, baseUrl);
  } catch {
    return null;
  }
}

function normalizeLegacyAuthorItHref(value: string): string {
  const trimmedValue = value.trim();
  const indexTopicMatch = /^index\.htm#([^?#]+\.htm(?:[?#].*)?)$/i.exec(
    trimmedValue,
  );
  return indexTopicMatch?.[1] ?? value;
}

function isHttpUrl(url: URL): boolean {
  return url.protocol === "http:" || url.protocol === "https:";
}

const VIDEO_ID = /^video_[A-Za-z0-9_-]+$/;
const VIDEO_TIME = /^(?:\d{1,2}:){1,2}\d{1,2}$|^\d{1,5}$/;
const CAO_ID = /^[A-Za-z0-9_-]{4,}$/;

function isRenderableArticlePath(pathname: string): boolean {
  const segments = pathname
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);

  if ((segments[0] ?? "").toLowerCase() === "video") {
    return isRenderableVideoPath(segments);
  }

  if ((segments[0] ?? "").toLowerCase() === "cao") {
    return isRenderableCaoPath(segments);
  }

  if (segments.length !== 4) return false;

  const [prefix = "", culture = "", routeSegment = "", finalSegment = ""] =
    segments;
  if (!allowedRoutePrefixes.has(prefix.toLowerCase())) return false;
  if (!/^(nl|en|fr)$/i.test(culture)) return false;
  if (!/^[a-z0-9_-]+$/i.test(routeSegment)) return false;
  return /^[^\\/:*?"<>|]+$/i.test(finalSegment);
}

function isRenderableVideoPath(segments: string[]): boolean {
  // video/{id} or video/{id}/{time}
  if (segments.length < 2 || segments.length > 3) return false;
  const id = (segments[1] ?? "").trim();
  if (!VIDEO_ID.test(id)) return false;
  if (segments.length === 3 && !VIDEO_TIME.test(segments[2] ?? ""))
    return false;
  return true;
}

function isRenderableCaoPath(segments: string[]): boolean {
  // cao/{id} — CAO release notes have no time segment.
  if (segments.length !== 2) return false;
  const id = (segments[1] ?? "").trim();
  // Mirrors the server ValidateParamsId guard: safe chars, length >= 4, has an underscore.
  return CAO_ID.test(id) && id.includes("_");
}

function getPathSearchHash(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}
